import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Trash2, Pencil, X, List, LayoutGrid, Plus } from 'lucide-react';
import { API_URL, API_ORIGIN } from '../config';

const emptyForm = {
  id: null,
  name: '',
  detail: '',
  currency: 'ARS',
  image: '',
  featured: false,
  variants: [
    { key: '18', price_base: '', price_promo: '' },
    { key: '24', price_base: '', price_promo: '' },
  ],
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

const imageSrc = (path) => (path && path.startsWith('/') ? `${API_ORIGIN}${path}` : path);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [products, setProducts] = useState([]);
  const [viewMode, setViewMode] = useState('cards');
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwMessage, setPwMessage] = useState('');

  const loadProducts = useCallback(() => {
    fetch(`${API_URL}/products.php`)
      .then((res) => res.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setError('No se pudieron cargar los productos'));
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/session.php`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (!data.authenticated) {
          navigate('/admin');
        } else {
          setChecking(false);
          loadProducts();
        }
      })
      .catch(() => navigate('/admin'));
  }, [navigate, loadProducts]);

  const handleLogout = async () => {
    await fetch(`${API_URL}/logout.php`, { method: 'POST', credentials: 'include' });
    navigate('/admin');
  };

  const startEdit = (product) => {
    setForm({
      id: product.id,
      name: product.name,
      detail: product.detail,
      currency: product.currency || 'ARS',
      image: product.image || '',
      featured: !!product.featured,
      variants: (product.variants || []).map((v) => ({
        key: v.key,
        price_base: v.price_base ?? '',
        price_promo: v.price_promo ?? '',
      })),
    });
    setImageFile(null);
    setImagePreview(product.image ? imageSrc(product.image) : '');
    setError('');
  };

  const cancelEdit = () => {
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview('');
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const updateVariant = (index, field, value) => {
    setForm((f) => ({
      ...f,
      variants: f.variants.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
    }));
  };

  const addVariant = () => {
    setForm((f) => ({ ...f, variants: [...f.variants, { key: '', price_base: '', price_promo: '' }] }));
  };

  const removeVariant = (index) => {
    setForm((f) => ({ ...f, variants: f.variants.filter((_, i) => i !== index) }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que querés borrar este producto?')) return;
    const res = await fetch(`${API_URL}/products.php?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.ok) {
      loadProducts();
      if (form.id === id) cancelEdit();
    } else {
      setError('No se pudo borrar el producto');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let imagePath = form.image;

      if (imageFile) {
        const fd = new FormData();
        fd.append('imagen', imageFile);
        const uploadRes = await fetch(`${API_URL}/upload.php`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        const uploadData = await uploadRes.json();
        if (!uploadRes.ok) throw new Error(uploadData.error || 'No se pudo subir la imagen');
        imagePath = uploadData.url;
      }

      const payload = {
        name: form.name,
        detail: form.detail,
        currency: form.currency,
        image: imagePath,
        featured: form.featured,
        variants: form.variants
          .filter((v) => v.key)
          .map((v) => ({
            key: v.key,
            price_base: parseFloat(v.price_base) || 0,
            price_promo: v.price_promo === '' ? null : parseFloat(v.price_promo),
          })),
      };

      const url = form.id
        ? `${API_URL}/products.php?id=${encodeURIComponent(form.id)}`
        : `${API_URL}/products.php`;
      const method = form.id ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo guardar el producto');

      cancelEdit();
      loadProducts();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMessage('');
    try {
      const res = await fetch(`${API_URL}/change_password.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current: pwCurrent, newPassword: pwNew }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo cambiar la contraseña');
      setPwMessage('Contraseña actualizada correctamente.');
      setPwCurrent('');
      setPwNew('');
    } catch (e) {
      setPwMessage(e.message);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Verificando sesión...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
              ← Volver al sitio
            </Link>
            <h1 className="text-3xl font-bold text-gray-800">Panel de Productos</h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-pink-600 hover:text-pink-700 font-medium"
          >
            <LogOut className="w-5 h-5" /> Salir
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Formulario */}
          <div className="bg-white rounded-2xl shadow-lg p-6 h-fit">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {form.id ? 'Editar producto' : 'Nuevo producto'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={form.detail}
                  onChange={(e) => setForm({ ...form, detail: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Tamaños y precios</label>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="flex items-center gap-1 text-xs text-pink-600 hover:text-pink-700"
                  >
                    <Plus className="w-3 h-3" /> Agregar tamaño
                  </button>
                </div>
                <div className="space-y-2">
                  {form.variants.map((v, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        type="text"
                        placeholder="cm"
                        value={v.key}
                        onChange={(e) => updateVariant(i, 'key', e.target.value)}
                        className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Precio"
                        value={v.price_base}
                        onChange={(e) => updateVariant(i, 'price_base', e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Promo (opcional)"
                        value={v.price_promo}
                        onChange={(e) => updateVariant(i, 'price_promo', e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeVariant(i)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Foto</label>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} />
                <p className="text-xs text-gray-400 mt-1">
                  Se redimensiona y comprime automáticamente al subirla, no importa el tamaño original.
                </p>
                {imagePreview && (
                  <img src={imagePreview} alt="Vista previa" className="mt-2 h-32 w-32 object-cover rounded-lg" />
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="w-5 h-5 accent-pink-500"
                />
                <span className="text-sm text-gray-700">Destacado (aparece en el slide de inicio)</span>
              </label>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white py-2 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : form.id ? 'Guardar cambios' : 'Agregar producto'}
                </button>
                {form.id && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Lista de productos */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold text-gray-800">
                {viewMode === 'cards' ? 'Últimos agregados' : 'Listado completo'}
              </h2>
              <button
                onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')}
                className="flex items-center gap-1 text-sm text-pink-600 hover:text-pink-700 font-medium border border-pink-200 rounded-lg px-3 py-1.5 bg-white"
              >
                {viewMode === 'cards' ? (
                  <>
                    <List className="w-4 h-4" /> Ver listado completo
                  </>
                ) : (
                  <>
                    <LayoutGrid className="w-4 h-4" /> Ver tarjetas
                  </>
                )}
              </button>
            </div>

            {products.length === 0 && (
              <p className="text-gray-500 text-center">Todavía no hay productos cargados.</p>
            )}

            {viewMode === 'cards' && (
              <div className="space-y-4">
                {[...products]
                  .sort((a, b) => new Date(b.creado_en || 0) - new Date(a.creado_en || 0))
                  .slice(0, 5)
                  .map((p) => (
                    <div key={p.id} className="bg-white rounded-2xl shadow-lg p-4 flex gap-4 items-center">
                      {p.image && (
                        <img
                          src={imageSrc(p.image)}
                          alt={p.name}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-800 truncate">
                          {p.name} {p.featured && <span className="text-xs text-pink-500">★ Destacado</span>}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {(p.variants || []).map((v) => `${v.key}cm: $${v.price_base}`).join(' · ')}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => startEdit(p)}
                          className="p-2 bg-gray-100 rounded-full hover:bg-gray-200"
                        >
                          <Pencil className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="p-2 bg-red-50 rounded-full hover:bg-red-100"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {viewMode === 'table' && products.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-left">
                    <tr>
                      <th className="px-3 py-2">Foto</th>
                      <th className="px-3 py-2">Torta</th>
                      <th className="px-3 py-2">Tamaños / precios</th>
                      <th className="px-3 py-2">Destacado</th>
                      <th className="px-3 py-2">Creado</th>
                      <th className="px-3 py-2">Modificado</th>
                      <th className="px-3 py-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {products.map((p) => (
                      <tr key={p.id}>
                        <td className="px-3 py-2">
                          {p.image && (
                            <img src={imageSrc(p.image)} alt={p.name} className="w-10 h-10 object-cover rounded-md" />
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{p.name}</td>
                        <td className="px-3 py-2">
                          {(p.variants || []).map((v) => (
                            <div key={v.key} className="whitespace-nowrap">
                              {v.key}cm: ${v.price_base}
                              {v.price_base_anterior != null && (
                                <span className="text-gray-400"> (antes ${v.price_base_anterior})</span>
                              )}
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2">{p.featured ? '★ Sí' : 'No'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(p.creado_en)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(p.modificado_en)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                startEdit(p);
                                setViewMode('cards');
                              }}
                              className="p-1.5 bg-gray-100 rounded-full hover:bg-gray-200"
                            >
                              <Pencil className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleDelete(p.id)}
                              className="p-1.5 bg-red-50 rounded-full hover:bg-red-100"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Cambiar contraseña */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-6 max-w-md">
          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            {showPasswordForm ? 'Ocultar' : 'Cambiar contraseña de acceso'}
          </button>
          {showPasswordForm && (
            <form onSubmit={handleChangePassword} className="space-y-3 mt-4">
              <input
                type="password"
                placeholder="Contraseña actual"
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              />
              <input
                type="password"
                placeholder="Nueva contraseña (mín. 8 caracteres)"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                minLength={8}
              />
              {pwMessage && <p className="text-sm text-gray-600">{pwMessage}</p>}
              <button
                type="submit"
                className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-900"
              >
                Actualizar contraseña
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
